import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { usePipelines, usePipelinesHealth } from 'api/pipelines';
import HealthStrip, { StripLegend } from 'components/pipelines/HealthStrip';
import { loadState, STATE, supportHref } from 'components/pipelines/loadStatus';
import { scheduleText } from 'components/pipelines/schedule';
import Verdict from 'components/pipelines/Verdict';
import { useTokens } from 'theme';
import { plural, whenInline, whenLabel } from 'utils/ru';
import useRole from 'hooks/useRole';
import { PreparationPanel, StorageSettingsPanel } from 'pages/console/warehouse/WarehousePage';

import { useTranslation } from 'react-i18next';

const COLUMNS = { xs: 'minmax(0,1fr) auto', md: 'minmax(0,1.6fr) minmax(0,1fr) 156px minmax(0,0.9fr) 110px' };

const lastRunTime = (run) => run && (run.finished_at || run.started_at || run.created_at);

export default function PipelinesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useRole();
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get('tab');
  const tab = ['loads', 'preparation', 'storage'].includes(requestedTab)
    ? requestedTab : searchParams.get('storage') === 'open' ? 'storage' : 'loads';

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    if (!next.has('storage')) return;
    next.delete('storage');
    if (tab === 'loads') next.delete('tab');
    else next.set('tab', tab);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, tab]);

  const changeTab = (_, value) => {
    const next = new URLSearchParams(searchParams);
    next.delete('storage');
    if (value === 'loads') next.delete('tab');
    else next.set('tab', value);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography component="h1" variant="h5" fontWeight={700}>{t('data.pipelines3')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {t('data.workspaceData.loadsSubtitle', { defaultValue: 'Поступление данных и их подготовка для отчётов.' })}
          </Typography>
        </Box>
        {tab === 'loads' && isAdmin && <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/console/pipelines/new')}>{t('data.newPipeline2')}</Button>}
      </Box>
      <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t('data.workspaceData.loadTabs')} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="loads" id="data-loads-tab" aria-controls="data-loads-panel" label={t('data.workspaceData.ingestion')} />
        <Tab value="preparation" id="data-preparation-tab" aria-controls="data-preparation-panel" label={t('data.workspaceData.preparation', { defaultValue: 'Подготовка данных' })} />
        <Tab value="storage" id="data-storage-tab" aria-controls="data-storage-panel" label={t('data.workspaceData.storage')} />
      </Tabs>
      <Box role="tabpanel" id="data-loads-panel" aria-labelledby="data-loads-tab" hidden={tab !== 'loads'}>
        {tab === 'loads' && <PipelineLoadsPanel canManage={isAdmin} />}
      </Box>
      <Box role="tabpanel" id="data-preparation-panel" aria-labelledby="data-preparation-tab" hidden={tab !== 'preparation'}>
        {tab === 'preparation' && <PreparationPanel />}
      </Box>
      <Box role="tabpanel" id="data-storage-panel" aria-labelledby="data-storage-tab" hidden={tab !== 'storage'}>
        {tab === 'storage' && <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.workspaceData.storageSummary')}</Typography>
          <StorageSettingsPanel />
        </>}
      </Box>
    </Box>
  );
}

export function PipelineLoadsPanel({ canManage = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeTokens = useTokens();
  const { data: pipelines, isLoading, error, isSuccess: pipelinesReady } = usePipelines();
  const { data: healthList, error: healthError, isSuccess: healthReady } = usePipelinesHealth();
  const healthById = Object.fromEntries((healthList || []).map((h) => [h.public_id, h]));

  const rows = (pipelines || []).map((pipeline) => {
    const health = healthById[pipeline.public_id];
    return { pipeline, health, state: loadState(pipeline, health) };
  });
  // A switched-off load is a choice, not a problem: failed, paused and late loads ask for attention.
  const problems = rows.filter((r) => ['failed', 'paused', 'late'].includes(r.state.key));
  const failing = problems.some((r) => r.state.key !== 'late');
  // Missing health data cannot confirm that every load is healthy. Compute
  // this over the complete list before any future display filtering.
  const completeHealth = rows.length > 0 && rows.every(({ health }) => health && ['ok', 'off', 'failed', 'paused', 'late'].includes(health.state));
  const allClear = pipelinesReady && healthReady && !error && !healthError && completeHealth
    && rows.every(({ pipeline, health, state }) => ['ok', 'off'].includes(health.state)
      && ['ok', 'off'].includes(state.key) && !pipeline.paused_by_billing);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{t('data.couldNotLoadPipelines')}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !rows.length ? (
        !error && <EmptyState onAdd={canManage ? () => navigate('/console/pipelines/new') : null} />
      ) : (
        <>
          {allClear && <Alert severity="success">{t('data.loadsAllClear')}</Alert>}
          {!error && !allClear && (healthError || (healthReady && !completeHealth)) && (
            <Alert severity="warning">{t('data.loadsHealthUnavailable')}</Alert>
          )}
          {!error && !healthReady && !healthError && <Alert severity="info">{t('data.loadsHealthLoading')}</Alert>}
          {problems.length > 0 && (
            <>
              <Verdict
                tone={failing ? 'bad' : 'warn'}
                title={t('data.valueValueAttention', { value1: problems.length, value2: plural(problems.length, [t('data.pipelineNeeds'), t('data.pipelinesNeed'), t('data.pipelinesNeed2')]) })}
                text={
                  rows.length > problems.length
                    ? t('data.theOtherValueHaveNoFailuresOrDelays', { value1: rows.length - problems.length })
                    : null
                }
              />
              <Paper sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>{t('data.needsAttention')}</Typography>
                {problems.map(({ pipeline, state }) => (
                  <AttentionRow key={pipeline.public_id} pipeline={pipeline} state={state} />
                ))}
              </Paper>
            </>
          )}

          <Paper sx={{ px: 1, py: 1.5 }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: COLUMNS.md,
                gap: 2,
                px: 1,
                pb: 1,
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: themeTokens.inkMuted,
              }}
            >
              <span>{t('data.pipeline2')}</span>
              <span>{t('data.schedule')}</span>
              <span>{t('data.last14')}</span>
              <span>{t('data.latestData')}</span>
              <span>{t('data.status')}</span>
            </Box>
            {rows.map(({ pipeline, health, state }) => (
              <ListRow key={pipeline.public_id} pipeline={pipeline} health={health} state={state} />
            ))}
            <Box sx={{ display: { xs: 'none', md: 'block' }, px: 1, pt: 1.25, mt: 0.5, borderTop: `1px solid ${themeTokens.border}` }}>
              <StripLegend />
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

function ListRow({ pipeline, health, state }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const s = STATE[state.key];
  const destination = pipeline.warehouse?.name || pipeline.destination_connection?.name;
  return (
    <Box
      component={RouterLink}
      to={`/console/pipelines/${pipeline.public_id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 2,
        alignItems: 'center',
        px: 1,
        py: 1.25,
        borderTop: `1px solid ${themeTokens.border}`,
        borderRadius: '8px',
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': { bgcolor: themeTokens.highlight },
        '&:focus-visible': { outline: `2px solid ${themeTokens.focus}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{pipeline.name}</Typography>
        <Typography noWrap sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted }}>
          {pipeline.source_connection?.name ? t('data.fromValue', { value1: pipeline.source_connection.name }) : ''}
          {destination ? t('data.toValue', { value1: destination }) : ''}
        </Typography>
      </Box>
      <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.85rem' }}>
        {scheduleText(pipeline.schedule_cron, undefined, pipeline.schedule_timezone)}
      </Typography>
      {/* The strip's cells open their own popover; a click on them must not open the load. */}
      <Box
        sx={{ display: { xs: 'none', md: 'block' } }}
        onClick={(e) => e.preventDefault()}
      >
        {health ? <HealthStrip health={health} /> : null}
      </Box>
      <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
        {health?.last_data_at ? whenLabel(health.last_data_at) : health ? t('data.noneYet') : '—'}
      </Typography>
      <Box>
        <Chip size="small" label={s.label} color={s.chip} />
      </Box>
    </Box>
  );
}

/** A failing load: the reason in one line and the one action that fixes it. */
function AttentionRow({ pipeline, state }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  let action = (
    <Button size="small" variant="outlined" component={RouterLink} to={`/console/pipelines/${pipeline.public_id}`}>{t('data.open')}</Button>
  );
  if (state.fix === 'source' && pipeline.source_connection?.public_id) {
    action = (
      <Button size="small" variant="contained" component={RouterLink} to={`/console/connectors/${pipeline.source_connection.public_id}`}>{t('data.openSource')}</Button>
    );
  } else if (state.fix === 'support') {
    action = (
      <Button size="small" variant="outlined" href={supportHref(pipeline.name, state.run)}>{t('data.contactSupport')}</Button>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        py: 1,
        borderTop: `1px solid ${themeTokens.border}`,
        '&:first-of-type': { borderTop: 'none', pt: 0 },
      }}
    >
      <Chip size="small" label={STATE[state.key].label} color={STATE[state.key].chip} />
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{pipeline.name}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: themeTokens.inkMuted }}>
          {state.reason}
          {state.run ? t('data.runValue', { value1: whenInline(lastRunTime(state.run)) }) : ''}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function EmptyState({ onAdd }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  return (
    <Paper sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>{t('data.noPipelinesYet')}</Typography>
      <Typography sx={{ fontSize: '0.9rem', color: themeTokens.inkMuted, mb: 3 }}>{t('data.aPipelineTakesDataFromASourceAndLoadsItInto')}</Typography>
      {onAdd && <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>{t('data.newPipeline2')}</Button>}
    </Paper>
  );
}
